import { html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { HelpLightElement } from "./help-light-element.js";
import { readJsonScript } from "../config.js";

interface CmsLoginInitialState {
  antiforgery?: { fieldName?: string; requestToken?: string };
  ip?: string;
  error?: string;
}

/**
 * Light-DOM login form for the internal /cms entry point. Renders a native
 * <form> that posts credentials to /cms/Login exactly like the legacy Razor
 * view. All authentication stays server-side: there is no fetch and no
 * client-side auth. Light DOM is required so the native form submit carries the
 * antiforgery hidden input without shadow-DOM form-association quirks.
 */
@customElement("help-jonas-cms-login")
export class HelpJonasCmsLogin extends HelpLightElement {
  @property({ attribute: "config-id" }) accessor configId =
    "cms-login-initial-state";

  @state() private accessor fieldName = "__RequestVerificationToken";
  @state() private accessor requestToken = "";
  @state() private accessor ip = "";
  @state() private accessor errorMessage = "";

  override connectedCallback(): void {
    super.connectedCallback();
    this.readInitialState();
  }

  private readInitialState(): void {
    try {
      const parsed = JSON.parse(
        readJsonScript(document, this.configId),
      ) as CmsLoginInitialState;
      if (parsed.antiforgery?.fieldName)
        this.fieldName = parsed.antiforgery.fieldName;
      if (parsed.antiforgery?.requestToken)
        this.requestToken = parsed.antiforgery.requestToken;
      if (typeof parsed.ip === "string") this.ip = parsed.ip;
      if (typeof parsed.error === "string") this.errorMessage = parsed.error;
    } catch {
      // No/invalid island: render the form with an empty token. The server
      // rejects the POST via [ValidateAntiForgeryToken]; never throw here.
    }
  }

  override render(): TemplateResult {
    return html`
      <style>
        help-jonas-cms-login {
          display: block;
          max-width: 420px;
          margin: 48px auto;
          padding: 0 16px;
          color: var(--color-text-on-background-800, #1a1a1a);
          font-family: var(--font-family-base, system-ui, sans-serif);
        }
        help-jonas-cms-login h1 {
          font-size: 1.5rem;
          margin: 0 0 24px;
        }
        help-jonas-cms-login .login {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 24px;
          border: 1px solid var(--color-foreground-300, #d0d0d0);
          border-radius: 8px;
          background: var(--color-background-800, #ffffff);
        }
        help-jonas-cms-login .login-row {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        help-jonas-cms-login .login-label {
          font-weight: 600;
        }
        help-jonas-cms-login .login-input input {
          width: 100%;
          box-sizing: border-box;
          padding: 8px 10px;
          border: 1px solid var(--color-foreground-300, #d0d0d0);
          border-radius: 4px;
          font: inherit;
        }
        help-jonas-cms-login input[type="submit"] {
          align-self: flex-start;
          padding: 10px 16px;
          border: 0;
          border-radius: 4px;
          background: var(--color-primary-600, #2b6cb0);
          color: #ffffff;
          font: inherit;
          font-weight: 600;
          cursor: pointer;
        }
        help-jonas-cms-login input[type="submit"]:hover {
          filter: brightness(0.95);
        }
        help-jonas-cms-login .login-error {
          padding: 10px 12px;
          border-radius: 4px;
          background: var(--color-danger-100, #fde8e8);
          color: var(--color-danger-700, #9b1c1c);
          border: 1px solid var(--color-danger-300, #f8b4b4);
        }
        help-jonas-cms-login .login-stats {
          margin-top: 8px;
          color: var(--color-foreground-500, #6b7280);
        }
      </style>
      <h1>Inloggen</h1>
      <form method="post" action="/cms/Login">
        <input
          type="hidden"
          name=${this.fieldName}
          .value=${this.requestToken}
        />
        <div class="login">
          ${this.errorMessage
            ? html`<div class="login-error" role="alert">
                ${this.errorMessage}
              </div>`
            : nothing}
          <div class="login-row">
            <div class="login-label">
              <label for="cms-login-username">Gebruikersnaam</label>
            </div>
            <div class="login-input">
              <input
                id="cms-login-username"
                type="text"
                name="UserName"
                autocomplete="username"
              />
            </div>
          </div>
          <div class="login-row">
            <div class="login-label">
              <label for="cms-login-password">Wachtwoord</label>
            </div>
            <div class="login-input">
              <input
                id="cms-login-password"
                type="password"
                name="Password"
                autocomplete="current-password"
              />
            </div>
          </div>
          <div class="login-row">
            <input type="submit" value="Inloggen" />
          </div>
        </div>
        <div class="login-stats"><small>${this.ip}</small></div>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-cms-login": HelpJonasCmsLogin;
  }
}
